import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Sparkles, Copy, Check, Settings, User, Bot, Compass } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAI } from '@/hooks/useAI';
import {
  generateReasoningPrompt,
  generateCodeFromReasoningPrompt,
  generateSyntaxCorrectionPrompt,
  generateAutonomousStrategyReasoningPrompt,
  generateAutonomousStrategyCodePrompt,
  getTradingStrategyAgentKnowledge,
  parseNextExplorations,
  parseStrategySummaryFromReasoning,
  type MarketSegment,
} from '@/services/ai/ai-prompts';
import type { ChatMessage } from '@/services/ai/chat-storage';
import apiClient from '@/services/api/client';
import { showSuccessToast, showErrorToast } from '@/utils/error-handler';
import MarkdownRenderer from '@/components/ui/markdown';

interface AIScriptGeneratorProps {
  /** Unified chat messages (shared with Chat AI panel). */
  chatMessages: ChatMessage[];
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  onCodeGenerated: (code: string) => void;
  existingSignals?: string[];
  datasetInfo?: {
    symbol?: string;
    timeframe?: string;
    columns?: string[];
  };
  onConfigureClick?: () => void; // Optional callback to open config modal
}

export default function AIScriptGenerator({
  chatMessages,
  setChatMessages,
  onCodeGenerated,
  existingSignals,
  datasetInfo,
  onConfigureClick
}: AIScriptGeneratorProps) {
  const navigate = useNavigate();
  const [userRequest, setUserRequest] = useState('');
  const [marketSegment, setMarketSegment] = useState<MarketSegment | ''>('');
  const [copied, setCopied] = useState(false);
  const [isReasoning, setIsReasoning] = useState(false);
  const [isCompiling, setIsCompiling] = useState(false);
  const [isCorrecting, setIsCorrecting] = useState(false);
  const [isAutonomous, setIsAutonomous] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { callAI, isLoading, error, isConfigured } = useAI({
    onError: (err) => {
      showErrorToast(err, 'Failed to generate script');
    }
  });

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, isLoading]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [userRequest]);

  const handleConfigure = () => {
    if (onConfigureClick) {
      onConfigureClick();
    } else {
      navigate('/settings');
    }
  };

  const handleGenerate = async () => {
    if (!userRequest.trim()) {
      showErrorToast('Please describe what you want to analyze');
      return;
    }

    if (!isConfigured) {
      showSuccessToast('Please configure your AI API key to generate scripts');
      handleConfigure();
      return;
    }

    const userMessage = userRequest.trim();
    
    // Add user message to unified chat
    const newUserMessage: ChatMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    };
    setChatMessages(prev => [...prev, newUserMessage]);
    setUserRequest(''); // Clear input

    // PHASE 1: Reasoning
    setIsReasoning(true);
    try {
      const reasoningPrompt = generateReasoningPrompt({
        userRequest: userMessage,
        existingSignals,
        datasetInfo
      });

      const reasoningSystemPrompt = `You are an expert quantitative analyst. Analyze the user's research request and provide clear, structured reasoning about what they are investigating and how to approach it.`;

      const reasoningResponse = await callAI(reasoningPrompt, reasoningSystemPrompt, {
        temperature: 0.7, // Higher temperature for more creative reasoning
        maxTokens: 1000
      });

      if (!reasoningResponse) {
        setIsReasoning(false);
        showErrorToast('No response from AI. Please try again.');
        return;
      }

      const reasoningText = reasoningResponse.trim();
      setIsReasoning(false);

      // Add reasoning message to chat
      const reasoningMessage: ChatMessage = {
        role: 'assistant',
        content: reasoningText,
        timestamp: new Date(),
        type: 'reasoning'
      };
      setChatMessages(prev => [...prev, reasoningMessage]);

      // PHASE 2: Code Generation
      const codePrompt = generateCodeFromReasoningPrompt({
        userRequest: userMessage,
        existingSignals,
        datasetInfo
      }, reasoningText);

      const codeSystemPrompt = `You are an expert QuantQuack DSL developer. Generate clean, production-ready DSL code based on the reasoning analysis. 
Only output the DSL code, no markdown, no explanations, no code blocks. Just the raw DSL code.`;

      const codeResponse = await callAI(codePrompt, codeSystemPrompt, {
        temperature: 0.3, // Lower temperature for more consistent code generation
        maxTokens: 2000
      });

      if (!codeResponse) {
        showErrorToast('No code generated. Please try again.');
        return;
      }

      // Clean up the response (remove markdown code blocks if present)
      let cleanedCode = codeResponse.trim();
      if (cleanedCode.startsWith('```')) {
        cleanedCode = cleanedCode.replace(/^```[\w]*\n?/, '').replace(/\n?```$/, '');
      }
      
      // Add code message to chat
      const codeMessage: ChatMessage = {
        role: 'assistant',
        content: cleanedCode,
        timestamp: new Date(),
        type: 'code'
      };
      setChatMessages(prev => [...prev, codeMessage]);

      // PHASE 3: Compilation and Correction
      setIsCompiling(true);
      
      try {
        // Try to compile the generated code
        const compileResult = await apiClient.post('/scripts/compile', { dslCode: cleanedCode });
        const compilation = compileResult.data;

        setIsCompiling(false);

        if (compilation.success) {
          // Compilation successful!
          onCodeGenerated(cleanedCode);
          showSuccessToast('Script generated and compiled successfully!');
          
          // Add success message
          const successMessage: ChatMessage = {
            role: 'assistant',
            content: '✅ Compilation successful! The code is ready to use.',
            timestamp: new Date(),
            type: 'code'
          };
          setChatMessages(prev => [...prev, successMessage]);
        } else if (compilation.errors && compilation.errors.length > 0) {
          // Compilation failed - trigger correction
          setIsCorrecting(true);
          
          const correctionPrompt = generateSyntaxCorrectionPrompt({
            originalCode: cleanedCode,
            compilationErrors: compilation.errors,
            reasoning: reasoningText,
            userRequest: userMessage
          });

          const correctionSystemPrompt = `You are an expert QuantQuack DSL developer specializing in fixing compilation errors. Fix all syntax errors in the DSL code.
Only output the corrected DSL code, no markdown, no explanations, no code blocks. Just the raw corrected DSL code.`;

          const correctedResponse = await callAI(correctionPrompt, correctionSystemPrompt, {
            temperature: 0.2, // Very low temperature for precise corrections
            maxTokens: 2000
          });

          setIsCorrecting(false);

          if (correctedResponse) {
            // Clean up corrected code
            let correctedCode = correctedResponse.trim();
            if (correctedCode.startsWith('```')) {
              correctedCode = correctedCode.replace(/^```[\w]*\n?/, '').replace(/\n?```$/, '');
            }

            // Add error message showing what was fixed
            const errorSummary = compilation.errors
              .map((e: any) => `Line ${e.line || '?'}: ${e.message}`)
              .join('\n');
            
            const errorMessage: ChatMessage = {
              role: 'assistant',
              content: `⚠️ Compilation errors detected:\n\`\`\`\n${errorSummary}\n\`\`\`\n\nFixing syntax errors...`,
              timestamp: new Date(),
              type: 'correction'
            };
            setChatMessages(prev => [...prev, errorMessage]);

            // Try compiling the corrected code
            setIsCompiling(true);
            const correctedCompileResult = await apiClient.post('/scripts/compile', { dslCode: correctedCode });
            const correctedCompilation = correctedCompileResult.data;
            setIsCompiling(false);

            if (correctedCompilation.success) {
              // Correction successful!
              
              const correctedCodeMessage: ChatMessage = {
                role: 'assistant',
                content: correctedCode,
                timestamp: new Date(),
                type: 'code'
              };
              setChatMessages(prev => [...prev, correctedCodeMessage]);

              const successMessage: ChatMessage = {
                role: 'assistant',
                content: '✅ Code corrected and compiled successfully!',
                timestamp: new Date(),
                type: 'correction'
              };
              setChatMessages(prev => [...prev, successMessage]);

              onCodeGenerated(correctedCode);
              showSuccessToast('Script corrected and compiled successfully!');
            } else {
              // Correction still has errors
              const stillErrorSummary = correctedCompilation.errors
                .map((e: any) => `Line ${e.line || '?'}: ${e.message}`)
                .join('\n');
              
              const stillErrorMessage: ChatMessage = {
                role: 'assistant',
                content: `❌ Still has compilation errors after correction:\n\`\`\`\n${stillErrorSummary}\n\`\`\`\n\nPlease review the code manually.`,
                timestamp: new Date(),
                type: 'correction'
              };
              setChatMessages(prev => [...prev, stillErrorMessage]);

              // Still provide the corrected code (user can fix manually)
              onCodeGenerated(correctedCode);
              showErrorToast('Code has compilation errors. Please review and fix manually.');
            }
          } else {
            setIsCorrecting(false);
            showErrorToast('Failed to correct syntax errors');
          }
        }
      } catch (error) {
        setIsCompiling(false);
        setIsCorrecting(false);
        console.error('Compilation error:', error);
        // Still provide the code even if compilation check fails
        onCodeGenerated(cleanedCode);
        showErrorToast('Could not verify compilation. Code generated but not validated.');
      }
    } catch (err) {
      console.error('Error in handleGenerate:', err);
      setIsReasoning(false);
      setIsCompiling(false);
      setIsCorrecting(false);
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred while generating script';
      showErrorToast(errorMessage, 'Failed to generate script');
    }
  };

  /** Autonomous strategy exploration: agent chooses or follows optional goal, generates strategy + next exploration ideas. */
  const handleAutonomousGenerate = async () => {
    if (!isConfigured) {
      showSuccessToast('Please configure your AI API key first');
      handleConfigure();
      return;
    }
    const goal = userRequest.trim();
    const userLabel = goal ? `Explore: ${goal}` : 'Generate strategy (autonomous)';
    setChatMessages((prev) => [...prev, { role: 'user', content: userLabel, timestamp: new Date() }]);
    setUserRequest('');
    setIsAutonomous(true);
    setIsReasoning(true);
    try {
      const recentStrategySummaries = chatMessages
        .filter((m): m is ChatMessage => m.role === 'assistant' && m.type === 'reasoning' && typeof m.content === 'string')
        .map((m) => parseStrategySummaryFromReasoning(m.content))
        .filter((s): s is string => s != null)
        .slice(-2);
      const reasoningPrompt = generateAutonomousStrategyReasoningPrompt({
        goal: goal || undefined,
        datasetInfo,
        recentStrategySummaries: recentStrategySummaries.length > 0 ? recentStrategySummaries : undefined,
        marketSegment: marketSegment || undefined,
      });
      const reasoningSystemPrompt = `${getTradingStrategyAgentKnowledge()}\n\nOutput your reasoning in the required format: STRATEGY, RATIONALE, NEXT_EXPLORATIONS (2-3 one-line ideas). No code yet.`;
      const reasoningResponse = await callAI(reasoningPrompt, reasoningSystemPrompt, { temperature: 0.7, maxTokens: 1200 });
      if (!reasoningResponse) {
        setIsReasoning(false);
        setIsAutonomous(false);
        showErrorToast('No response from AI. Please try again.');
        return;
      }
      const reasoningText = reasoningResponse.trim();
      const nextExplorations = parseNextExplorations(reasoningText);
      setIsReasoning(false);
      setChatMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: reasoningText,
          timestamp: new Date(),
          type: 'reasoning',
          nextExplorations: nextExplorations.length > 0 ? nextExplorations : undefined,
        },
      ]);
      const codePrompt = generateAutonomousStrategyCodePrompt({ goal: goal || undefined, datasetInfo }, reasoningText);
      const codeSystemPrompt = `${getTradingStrategyAgentKnowledge()}\n\nOutput ONLY the complete raw DSL code. No markdown, no explanation.`;
      const codeResponse = await callAI(codePrompt, codeSystemPrompt, { temperature: 0.3, maxTokens: 4000 });
      if (!codeResponse) {
        setIsAutonomous(false);
        showErrorToast('No code generated. Please try again.');
        return;
      }
      let cleanedCode = codeResponse.trim();
      if (cleanedCode.startsWith('```')) {
        cleanedCode = cleanedCode.replace(/^```[\w]*\n?/, '').replace(/\n?```$/, '');
      }
      setChatMessages((prev) => [...prev, { role: 'assistant', content: cleanedCode, timestamp: new Date(), type: 'code' }]);
      setIsCompiling(true);
      try {
        const compileResult = await apiClient.post('/scripts/compile', { dslCode: cleanedCode });
        const compilation = compileResult.data;
        setIsCompiling(false);
        if (compilation.success) {
          onCodeGenerated(cleanedCode);
          showSuccessToast('Strategy generated and compiled successfully');
        } else if (compilation.errors?.length) {
          setIsCorrecting(true);
          const correctionPrompt = generateSyntaxCorrectionPrompt({
            originalCode: cleanedCode,
            compilationErrors: compilation.errors.map((e: any) => ({ line: e.line, column: e.column, message: e.message, type: e.type })),
            userRequest: goal || 'Autonomous strategy',
          });
          const correctionSystemPrompt = `You are an expert QuantQuack DSL developer. Fix all compilation errors. Output ONLY the corrected DSL code.`;
          const corrected = await callAI(correctionPrompt, correctionSystemPrompt, { temperature: 0.2, maxTokens: 4000 });
          setIsCorrecting(false);
          if (corrected) {
            let fixed = corrected.trim();
            if (fixed.startsWith('```')) fixed = fixed.replace(/^```[\w]*\n?/, '').replace(/\n?```$/, '');
            const retryCompile = await apiClient.post('/scripts/compile', { dslCode: fixed });
            if (retryCompile.data.success) {
              setChatMessages((prev) => [...prev, { role: 'assistant', content: fixed, timestamp: new Date(), type: 'code' }]);
              onCodeGenerated(fixed);
              showSuccessToast('Strategy corrected and compiled');
            } else {
              onCodeGenerated(fixed);
              showErrorToast('Some errors remain. Code applied for manual fix.');
            }
          }
        }
      } catch {
        setIsCompiling(false);
        setIsCorrecting(false);
        onCodeGenerated(cleanedCode);
        showErrorToast('Could not verify compilation. Code applied.');
      }
      setIsAutonomous(false);
    } catch (err) {
      setIsReasoning(false);
      setIsCompiling(false);
      setIsCorrecting(false);
      setIsAutonomous(false);
      showErrorToast(err instanceof Error ? err.message : 'Autonomous generation failed');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleGenerate();
    }
  };


  return (
    <Card className="flex flex-col h-full min-h-[600px]">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          AI Script Generation
        </CardTitle>
        <CardDescription>
          Describe the statistics you want to calculate. AI generates the DSL code for you.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col min-h-0 p-0">
        {!isConfigured && (
          <div className="p-4 border-b">
            <Alert>
              <AlertDescription className="flex items-center justify-between">
                <span>AI is not configured. Configure your API key to use script generation.</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleConfigure}
                  className="ml-4"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Configure AI
                </Button>
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Chat Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
          {chatMessages.length === 0 && (
            <div className="flex items-center justify-center h-full text-center">
              <div className="space-y-2 max-w-md">
                <Sparkles className="w-8 h-8 mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Start a conversation to generate DSL code. Describe the statistics you want to calculate.
                </p>
                <p className="text-xs text-muted-foreground mt-4">
                  Examples:
                </p>
                <ul className="text-xs text-muted-foreground space-y-1 text-left list-disc list-inside">
                  <li>Calculate average volume by hour of day</li>
                  <li>Find correlation between price changes and volume</li>
                  <li>Calculate volatility statistics by trading session</li>
                </ul>
              </div>
            </div>
          )}

          {chatMessages.map((message, index) => (
            <div
              key={index}
              className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {message.role === 'assistant' && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-primary" />
                </div>
              )}
              <div
                className={`max-w-[85%] rounded-lg p-3 ${
                  message.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted'
                }`}
              >
                {message.role === 'user' ? (
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                ) : message.type === 'reasoning' ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-blue-500" />
                      <pre className="text-xs text-muted-foreground font-semibold">Analysis Plan</pre>
                    </div>
                    <div className="text-sm bg-background/50 p-3 rounded border">
                      <MarkdownRenderer content={message.content} className="text-sm" />
                    </div>
                    {message.nextExplorations && message.nextExplorations.length > 0 && (
                      <div className="mt-3 pt-2 border-t border-border">
                        <p className="text-xs text-muted-foreground font-medium mb-2">Next explorations</p>
                        <div className="flex flex-wrap gap-2">
                          {message.nextExplorations.map((idea, i) => (
                            <Button
                              key={i}
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs font-normal"
                              onClick={() => setUserRequest(`NEW EXPLORATION: ${idea}`)}
                            >
                              <Compass className="w-3 h-3 mr-1.5" />
                              {idea}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : message.type === 'correction' ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-orange-500" />
                      <pre className="text-xs text-muted-foreground font-semibold">Syntax Correction</pre>
                    </div>
                    <div className="text-sm bg-background/50 p-3 rounded border">
                      <MarkdownRenderer content={message.content} className="text-sm" />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      <pre className="text-xs text-muted-foreground font-semibold">Generated DSL Code</pre>
                    </div>
                    <pre className="text-sm overflow-x-auto whitespace-pre-wrap font-mono bg-background/50 p-2 rounded border">
                      <code>{message.content}</code>
                    </pre>
                    <div className="flex gap-2 mt-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(message.content);
                          setCopied(true);
                          showSuccessToast('Code copied to clipboard');
                          setTimeout(() => setCopied(false), 2000);
                        }}
                        className="h-7 text-xs"
                      >
                        {copied ? (
                          <>
                            <Check className="w-3 h-3 mr-1" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3 mr-1" />
                            Copy
                          </>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          onCodeGenerated(message.content);
                          showSuccessToast('Code applied to editor');
                        }}
                        className="h-7 text-xs"
                      >
                        Use in Editor
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              {message.role === 'user' && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-4 h-4 text-primary" />
                </div>
              )}
            </div>
          ))}

          {(isLoading || isReasoning || isCompiling || isCorrecting) && (
            <div className="flex gap-3 justify-start">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Bot className="w-4 h-4 text-primary" />
              </div>
              <div className="bg-muted rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm text-muted-foreground">
                    {isReasoning 
                      ? 'Analyzing your request...' 
                      : isCompiling 
                      ? 'Compiling code...' 
                      : isCorrecting 
                      ? 'Fixing syntax errors...' 
                      : 'Generating code...'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {error && (
            <Alert variant="destructive" className="mx-4">
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t p-4 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground whitespace-nowrap">Market (for suggestions):</span>
            <Select
              value={marketSegment || 'none'}
              onValueChange={(v) => setMarketSegment(v === 'none' ? '' : (v as MarketSegment))}
            >
              <SelectTrigger className="w-[180px] h-9">
                <SelectValue placeholder="Not specified" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not specified</SelectItem>
                <SelectItem value="forex">Forex</SelectItem>
                <SelectItem value="equities">US Equities</SelectItem>
                <SelectItem value="futures">Futures</SelectItem>
                <SelectItem value="crypto">Crypto</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Textarea
              ref={textareaRef}
              placeholder='e.g., "Calculate average volume by hour of day" or leave empty and click Explore for autonomous strategy'
              value={userRequest}
              onChange={(e) => setUserRequest(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              className="resize-none min-h-[44px] max-h-[200px]"
            />
            {/* Explore: only when textarea empty or loading for Explore */}
            {(!userRequest.trim() || (isLoading && isAutonomous)) && (
              <Button
                onClick={handleAutonomousGenerate}
                disabled={isLoading || !isConfigured}
                size="icon"
                className="h-[44px] w-[44px] flex-shrink-0"
                title="Explore strategy (autonomous)"
              >
                {isLoading && isAutonomous ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Compass className="w-4 h-4" />
                )}
              </Button>
            )}
            {/* Generate from description: only when textarea has text or loading for Generate */}
            {(userRequest.trim() || (isLoading && !isAutonomous)) && (
              <Button
                onClick={handleGenerate}
                disabled={isLoading || !userRequest.trim() || !isConfigured}
                size="icon"
                className="h-[44px] w-[44px] flex-shrink-0"
                title="Generate from description"
              >
                {isLoading && !isAutonomous ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Press <kbd className="px-1.5 py-0.5 text-xs font-semibold bg-muted rounded">⌘</kbd> + <kbd className="px-1.5 py-0.5 text-xs font-semibold bg-muted rounded">Enter</kbd> to send
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
